import React, { Fragment, useState, useEffect } from 'react';
import Parse from 'parse';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { bethelFileUrl, bethelRun } from '../../api/config';
import { Banner, Button, Card, CardHeader, EmptyState, Spinner } from '../ui';

// Read a File as base64 (no data-URL prefix) so it can be handed to a cloud
// function, which uploads it to the app server for us.
const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

/**
 * A document-sized preview: the PDF embedded right in the page, with a link to
 * open it full-screen in a new tab. Wider than the shared Modal, which is
 * locked to a form width.
 */
const PdfPreview = ({ open, title, url, onClose }) => (
    <Transition show={open} as={Fragment}>
        <Dialog onClose={onClose} className="relative z-50">
            <TransitionChild
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-neutral-900/50" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
                <div className="flex min-h-full items-center justify-center">
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 translate-y-2 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-2 sm:scale-95"
                    >
                        <DialogPanel className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                            <div className="flex items-center justify-between gap-4 border-b border-neutral-200 px-5 py-3">
                                <DialogTitle className="min-w-0 truncate font-display text-base font-semibold text-neutral-900">
                                    {title}
                                </DialogTitle>
                                <div className="flex flex-shrink-0 items-center gap-2">
                                    {url && (
                                        <Button as="a" href={url} target="_blank" rel="noopener noreferrer" size="sm" variant="secondary">
                                            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                                            Open in new tab
                                        </Button>
                                    )}
                                    <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close preview">
                                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                    </Button>
                                </div>
                            </div>
                            <div className="min-h-0 flex-1 bg-neutral-100">
                                {url ? (
                                    <iframe src={url} title={title} className="h-full w-full border-0" />
                                ) : (
                                    <div className="flex h-full items-center justify-center text-sm text-neutral-500">
                                        This bulletin has no file attached.
                                    </div>
                                )}
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </Transition>
);

const BulletinManager = () => {
    const [bulletins, setBulletins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [preview, setPreview] = useState(null); // { title, url }

    useEffect(() => {
        fetchBulletins();
    }, []);

    const fetchBulletins = async () => {
        try {
            setLoading(true);
            setError('');

            // Direct fetch to the app server — no global SDK switch. See
            // bethelRun in api/config.js for why.
            const bulletinsData = await bethelRun('getBulletins');
            setBulletins(bulletinsData || []);
        } catch (err) {
            console.error('Error fetching bulletins:', err);
            setError('We could not load the bulletins. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        e.preventDefault();
        setUploading(true);
        setError('');
        setSuccessMessage('');

        const title = e.target.title.value;
        const date = e.target.date.value;
        const file = e.target.file.files[0];
        const form = e.target;

        if (!file) {
            setError('Please choose a file to upload.');
            setUploading(false);
            return;
        }

        try {
            // The forms server verifies we're a pastor and writes it to the app.
            const fileBase64 = await fileToBase64(file);
            await Parse.Cloud.run('pastorsUploadBulletin', {
                title,
                date,
                fileName: file.name,
                fileBase64,
                contentType: file.type || 'application/pdf',
            });

            setSuccessMessage('Bulletin uploaded.');
            form.reset();
            fetchBulletins();
        } catch (err) {
            console.error('Error uploading bulletin:', err);
            setError(err.message || 'We could not upload the bulletin. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (bulletin) => {
        if (!window.confirm(`Delete "${bulletin.get('bulletinTitle')}"? This can't be undone.`)) {
            return;
        }

        try {
            await Parse.Cloud.run('pastorsDeleteBulletin', { bulletinId: bulletin.id });
            setSuccessMessage('Bulletin deleted.');
            fetchBulletins();
        } catch (err) {
            console.error('Error deleting bulletin:', err);
            setError(err.message || 'We could not delete the bulletin. Please try again.');
        }
    };

    return (
        <div className="space-y-4">
            {error && <Banner tone="danger">{error}</Banner>}
            {successMessage && <Banner tone="success">{successMessage}</Banner>}

            <Card>
                <CardHeader title="Upload a bulletin" description="It appears in the member app once uploaded." />
                <form onSubmit={handleFileUpload} className="grid gap-4 px-5 py-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                        <label htmlFor="bulletin-title" className="block text-sm font-medium text-neutral-800">
                            Title
                        </label>
                        <input
                            id="bulletin-title"
                            type="text"
                            name="title"
                            required
                            className="mt-1"
                            placeholder="Weekly Bulletin — January 7, 2026"
                        />
                    </div>
                    <div>
                        <label htmlFor="bulletin-date" className="block text-sm font-medium text-neutral-800">
                            Date
                        </label>
                        <input
                            id="bulletin-date"
                            type="date"
                            name="date"
                            required
                            className="mt-1"
                            defaultValue={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                    <div>
                        <label htmlFor="bulletin-file" className="block text-sm font-medium text-neutral-800">
                            File (PDF)
                        </label>
                        <input id="bulletin-file" type="file" name="file" accept=".pdf" required className="mt-1" />
                    </div>
                    <div className="sm:col-span-2">
                        <Button type="submit" variant="primary" disabled={uploading}>
                            {uploading && <Spinner className="h-4 w-4" />}
                            {uploading ? 'Uploading…' : 'Upload bulletin'}
                        </Button>
                    </div>
                </form>
            </Card>

            <Card className="overflow-hidden">
                <CardHeader title="Bulletins" description="Newest first." />
                {loading ? (
                    <EmptyState title="Loading bulletins…" />
                ) : bulletins.length === 0 ? (
                    <EmptyState title="No bulletins yet" description="Upload your first bulletin above." />
                ) : (
                    <ul className="divide-y divide-neutral-100">
                        {bulletins.map((bulletin) => {
                            const url = bethelFileUrl(bulletin.get('bulletinFile'));
                            return (
                                <li
                                    key={bulletin.id}
                                    className="flex flex-col gap-2 px-5 py-3.5 hover:bg-neutral-50/70 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-neutral-900">
                                            {bulletin.get('bulletinTitle')}
                                        </p>
                                        <p className="mt-0.5 text-xs text-neutral-500">
                                            {formatDate(bulletin.get('bulletinDate'))}
                                        </p>
                                    </div>
                                    <div className="flex flex-shrink-0 items-center gap-2">
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            disabled={!url}
                                            onClick={() => setPreview({ title: bulletin.get('bulletinTitle'), url })}
                                        >
                                            View
                                        </Button>
                                        <Button size="sm" variant="danger" onClick={() => handleDelete(bulletin)}>
                                            Delete
                                        </Button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Card>

            <PdfPreview
                open={!!preview}
                title={preview?.title || ''}
                url={preview?.url}
                onClose={() => setPreview(null)}
            />
        </div>
    );
};

export default BulletinManager;
